import {
  Badge,
  Box,
  Code,
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ToolCall } from "core";
import React from "react";

interface ToolCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  toolCall: ToolCall;
}

export default function ToolCallModal({
  isOpen,
  onClose,
  toolCall,
}: ToolCallModalProps) {
  const getStatusColor = (status?: string) => {
    switch (status) {
      case "success":
        return "green";
      case "error":
        return "red";
      case "started":
        return "yellow";
      default:
        return "gray";
    }
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return "—";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const formatJson = (data: unknown) => {
    if (data === undefined || data === null) return "—";
    try {
      return JSON.stringify(data, null, 2);
    } catch (e) {
      return String(data);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>
          <HStack>
            <Text>Tool Call:</Text>
            <Code colorScheme="blue" fontSize="md">
              {toolCall.name}
            </Code>
            {toolCall.status && (
              <Badge colorScheme={getStatusColor(toolCall.status)} ml={2}>
                {toolCall.status}
              </Badge>
            )}
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto" pb={6}>
          <VStack spacing={4} align="stretch">
            {/* Tool Name and Status */}
            <Box>
              <Text fontWeight="bold" mb={2}>
                Tool Information
              </Text>
              <VStack align="stretch" spacing={2} pl={2}>
                <HStack>
                  <Text fontWeight="medium" minW="120px">
                    Name:
                  </Text>
                  <Code>{toolCall.name}</Code>
                </HStack>
                <HStack>
                  <Text fontWeight="medium" minW="120px">
                    Role:
                  </Text>
                  <Badge>{toolCall.role}</Badge>
                </HStack>
                {toolCall.status && (
                  <HStack>
                    <Text fontWeight="medium" minW="120px">
                      Status:
                    </Text>
                    <Badge colorScheme={getStatusColor(toolCall.status)}>
                      {toolCall.status}
                    </Badge>
                  </HStack>
                )}
              </VStack>
            </Box>

            <Divider />

            {/* Timestamps */}
            {(toolCall.startedAt || toolCall.completedAt) && (
              <>
                <Box>
                  <Text fontWeight="bold" mb={2}>
                    Timing
                  </Text>
                  <VStack align="stretch" spacing={2} pl={2}>
                    {toolCall.startedAt && (
                      <HStack>
                        <Text fontWeight="medium" minW="120px">
                          Started:
                        </Text>
                        <Text fontSize="sm">{formatTimestamp(toolCall.startedAt)}</Text>
                      </HStack>
                    )}
                    {toolCall.completedAt && (
                      <HStack>
                        <Text fontWeight="medium" minW="120px">
                          Completed:
                        </Text>
                        <Text fontSize="sm">{formatTimestamp(toolCall.completedAt)}</Text>
                      </HStack>
                    )}
                  </VStack>
                </Box>
                <Divider />
              </>
            )}

            {/* Arguments */}
            <Box>
              <Text fontWeight="bold" mb={2}>
                Arguments
              </Text>
              <Box
                bg="gray.50"
                p={3}
                borderRadius="md"
                overflowX="auto"
                maxH="300px"
                overflowY="auto"
              >
                <Code
                  display="block"
                  whiteSpace="pre"
                  fontSize="sm"
                  bg="transparent"
                  p={0}
                >
                  {formatJson(toolCall.args)}
                </Code>
              </Box>
            </Box>

            <Divider />

            {/* Result */}
            <Box>
              <Text fontWeight="bold" mb={2}>
                Result
              </Text>
              <Box
                bg={toolCall.status === "error" ? "red.50" : "gray.50"}
                p={3}
                borderRadius="md"
                overflowX="auto"
                maxH="300px"
                overflowY="auto"
              >
                <Code
                  display="block"
                  whiteSpace="pre"
                  fontSize="sm"
                  bg="transparent"
                  p={0}
                  color={toolCall.status === "error" ? "red.700" : "inherit"}
                >
                  {formatJson(toolCall.result)}
                </Code>
              </Box>
            </Box>

            {/* Error Message */}
            {toolCall.errorMessage && (
              <>
                <Divider />
                <Box>
                  <Text fontWeight="bold" mb={2} color="red.600">
                    Error Message
                  </Text>
                  <Box bg="red.50" p={3} borderRadius="md">
                    <Text fontSize="sm" color="red.700">
                      {toolCall.errorMessage}
                    </Text>
                  </Box>
                </Box>
              </>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
